import type { ReactElement } from 'react';
import type { LucideIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import styles from './Icon.module.scss';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'inherit';
export type IconColor = 'current' | 'muted' | 'accent' | 'primary' | 'success' | 'error' | 'warning';

export interface IconProps {
    /** The icon component, e.g. `Tag` from `lucide-react`. */
    icon: LucideIcon;
    /** Size from the icon scale; `inherit` follows the font size around it (1em). @default 'md' */
    size?: IconSize;
    /** Colour role; `current` takes the text colour. @default 'current' */
    color?: IconColor;
    /** Accessible name for an icon that means something on its own; without it the icon is hidden from screen readers. */
    label?: string;
    /** Line width of the icon. */
    strokeWidth?: number;
    /** Places the icon (margins, transforms); not for its size or colour. */
    className?: string;
}

/**
 * An icon with its size and colour from the design tokens. Use it for an icon on its own; components that hold an
 * icon (`Button`, `IconButton`, `Tag`…) size theirs themselves.
 *
 * @example
 * <Icon icon={TagIcon} size="2xl" color="accent" />
 */
export function Icon({
    icon: IconComponent,
    size = 'md',
    color = 'current',
    label,
    strokeWidth,
    className,
}: IconProps): ReactElement {
    return (
        <IconComponent
            className={classNames(
                styles.icon,
                styles[`size-${size}`],
                color !== 'current' && styles[`color-${color}`],
                className,
            )}
            strokeWidth={strokeWidth}
            {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
        />
    );
}
